import React from 'react';
import {
    Select,
    Input,
    DateInput,
    OnOffSwitch,
    CheckBox,
} from '../Common';
import { TranslateW } from '../../Utils/Languages/Translate';
import { Grid, Stack, Box, Slider, Typography, Divider } from '@mui/material';
import { trakrefBlue, Grey8, Grey3, red } from '../../color_utils';
import UserInfo from "../../UserInfo";

const Form = ({
    index,
    workOrder,
    errors,
    handleWorkOrderFieldData,
    isEditing,
    selectOptions,
    useMyLocation,
    locationRadius,
    onUseMyLocationChange,
    onRadiusChange,
    nearbyLocationsMessage,
    isLoadingLocations,
}) => (
    <>
        <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: { xs: 0, md: 4 } }}>
            <Grid item xs={12} md={6} sx={{ marginBottom: '1%' }}>
                <Stack spacing={1}>
                    <Select
                        id='WorkOrderType_Dropdown'
                        required
                        label={TranslateW("createWorkOrder.work-order-type")}
                        options={selectOptions.types}
                        error={errors.type}
                        value={workOrder.type}
                        onChange={e => handleWorkOrderFieldData(index, 'type', e.target.value, true)}
                        width={100}
                    />
                    <div>
                        <Input
                            id="WorkOrderNumber_Input"
                            fullWidth
                            required={UserInfo.isWorkOrderNumberRequired()}
                            maxCharacterLength={50}
                            value={workOrder.workOrderNumber}
                            label={TranslateW("createWorkOrder.work-order-number")}
                            onChange={e => handleWorkOrderFieldData(index, 'workOrderNumber', e.target.value)}
                            error={errors.workOrderNumber}
                            helperText={errors.workOrderNumber}
                        />
                        <div className={`WONumberCounter ${(workOrder.workOrderNumber && workOrder.workOrderNumber.length > 50) ? 'woNumberExceed' : ''}`}>
                            {workOrder.workOrderNumber ? workOrder.workOrderNumber.length : 0}/50
                        </div>
                    </div>
                    <Input
                        id="PurchaseOrderNumber_Input"
                        fullWidth
                        label={TranslateW("workOrderDetailsMain.purchase-order-number")}
                        value={workOrder.purchaseOrderNumber}
                        onChange={e => handleWorkOrderFieldData(index, 'purchaseOrderNumber', e.target.value)}
                        error={errors.purchaseOrderNumber}
                    />
                    <Divider sx={{ width: '100%', pt: 2, display: { lg: 'none' } }} />
                    {!isEditing && (
                        <Box sx={{ display: { lg: 'none' } }}>
                            <CheckBox
                                label="Use my Location"
                                checked={useMyLocation}
                                onChange={(e) => onUseMyLocationChange(e.target.checked)}
                                labelStyle={{ marginBottom: 0 }}
                            />
                            {useMyLocation && (
                                <Box sx={{ px: 2, mt: 2}}>
                                    <Slider
                                        value={locationRadius}
                                        onChange={(_, val) => onRadiusChange(val)}
                                        min={1}
                                        max={500}
                                        step={null}
                                        valueLabelDisplay="on"
                                        valueLabelFormat={(value) => `${value} mi`}
                                        marks={[
                                            { value: 1, label: '1 mi' },
                                            ...[...Array(50)].map((_, i) => ({ value: (i + 1) * 10 })),
                                            { value: 500, label: '500 mi' }
                                        ]}
                                        sx={{
                                            color: trakrefBlue,
                                            '& .MuiSlider-thumb': {
                                                backgroundColor: trakrefBlue,
                                            },
                                            '& .MuiSlider-track': {
                                                backgroundColor: trakrefBlue,
                                            },
                                            '& .MuiSlider-valueLabel': {
                                                backgroundColor: Grey8,
                                                borderRadius: 1,
                                                padding: '4px 12px',
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                    <Select
                        id="Location_Dropdown"
                        required
                        label={TranslateW("kanbanFilters.location")}
                        disabled={isEditing || isLoadingLocations}
                        onChange={e => handleWorkOrderFieldData(index, 'locationId', e.target.value)}
                        error={errors.locationId}
                        value={workOrder.locationId}
                        options={selectOptions.locations}
                        width={100}
                    />
                    {useMyLocation && nearbyLocationsMessage && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: nearbyLocationsMessage.includes('No nearby') ? red : Grey3,
                                display: 'block',
                                mt: 0.5,
                                whiteSpace: 'pre-line'
                            }}
                        >
                            {nearbyLocationsMessage}
                        </Typography>
                    )}
                    <Divider sx={{ width: '100%', py: 1, display: { lg: 'none' } }} />
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <Stack spacing={1}>
                            <Select
                                id="ServiceCompany_Dropdown"
                                required
                                label={TranslateW("workOrderDetailsMain.service-company")}
                                disabled={isEditing}
                                value={workOrder.serviceCompanyId}
                                onChange={e => handleWorkOrderFieldData(index, 'serviceCompanyId', e.target.value)}
                                error={errors.serviceCompanyId}
                                options={selectOptions.serviceCompanies}
                                width={100}
                            />
                            <DateInput
                                id="ScheduledDate_DatePicker"
                                required
                                allowPast
                                allowFuture
                                label={`${TranslateW("workOrderRepeater.scheduled-date")}*`}
                                date={workOrder.scheduledDate}
                                onChange={date => handleWorkOrderFieldData(index, 'scheduledDate', date)}
                                error={errors.scheduledDate}
                                width={100}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ marginBottom: '1%' }}>
                <Stack spacing={1}>
                    <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                        <Stack spacing={1}>
                            <Select
                                id="ServiceCompany_Dropdown_Mobile"
                                required
                                label={TranslateW("workOrderDetailsMain.service-company")}
                                disabled={isEditing}
                                value={workOrder.serviceCompanyId}
                                onChange={e => handleWorkOrderFieldData(index, 'serviceCompanyId', e.target.value)}
                                error={errors.serviceCompanyId}
                                options={selectOptions.serviceCompanies}
                                width={100}
                            />
                            <DateInput
                                id="ScheduledDate_DatePicker_Mobile"
                                required
                                allowPast
                                allowFuture
                                label={`${TranslateW("workOrderRepeater.scheduled-date")}*`}
                                date={workOrder.scheduledDate}
                                onChange={date => handleWorkOrderFieldData(index, 'scheduledDate', date)}
                                error={errors.scheduledDate}
                                width={100}
                            />
                        </Stack>
                    </Box>
                    {
                        isEditing && (
                            <Select
                                id='StatusReason_Dropdown'
                                label={TranslateW("workOrderDetailsMain.status-reason")}
                                disabled={workOrder.statusReason === 48}
                                value={workOrder.statusReason}
                                onChange={e => handleWorkOrderFieldData(index, 'statusReason', e.target.value)}
                                options={selectOptions.statusReasonOptions}
                                width={100}
                            />
                        )
                    }
                    {
                        workOrder.type === 1 ? (
                            <>
                                <DateInput
                                    id='Duedate_DatePicker'
                                    allowPast
                                    allowFuture
                                    label={`${TranslateW("workOrderRepeater.due-date")}`}
                                    date={workOrder.dueDate}
                                    onChange={date => handleWorkOrderFieldData(index, 'dueDate', date)}
                                    width={100}
                                />
                                <Input
                                    id="WorkScheduled_Input"
                                    required
                                    multiLine
                                    rows={4}
                                    fullWidth
                                    value={workOrder.workScheduled}
                                    label={TranslateW("workOrderRepeater.work-scheduled")}
                                    onChange={e => handleWorkOrderFieldData(index, 'workScheduled', e.target.value)}
                                    error={errors.workScheduled}
                                />
                            </>
                        ) : (
                            <>
                                <Select
                                    id='Priority_Dropdown'
                                    required
                                    label={TranslateW("workOrdersView.priority")}
                                    disabled={!workOrder.type}
                                    value={workOrder.priorityId}
                                    onChange={e => handleWorkOrderFieldData(index, 'priorityId', e.target.value)}
                                    options={selectOptions.priorities}
                                    error={errors.priorityId}
                                    width={100}
                                />
                                <Input
                                    id="ProblemDescription_Input"
                                    required
                                    multiLine
                                    rows={4}
                                    fullWidth
                                    value={workOrder.priorityDescription}
                                    label={TranslateW("createWorkOrder.problem-description")}
                                    error={errors.priorityDescription}
                                    onChange={e => handleWorkOrderFieldData(index, 'priorityDescription', e.target.value)}
                                    disabled={!workOrder.type}
                                />
                            </>
                        )
                    }
                    <Box sx={{ display: UserInfo.isTechnician() ? { xs: 'none', lg: 'block' } : 'block' }}>
                        <Stack direction={"row"} spacing={1}>
                            <Input
                                fullWidth
                                id="LowDollarLimit_Input"
                                type="number"
                                value={workOrder.lowDollarLimit}
                                label={TranslateW("lowDollarLimit")}
                                onChange={e => handleWorkOrderFieldData(index, 'lowDollarLimit', e.target.value)}
                            />
                            <Input
                                fullWidth
                                id="HighDollarLimit_Input"
                                type="number"
                                value={workOrder.highDollarLimit}
                                label={TranslateW("highDollarLimit")}
                                onChange={e => handleWorkOrderFieldData(index, 'highDollarLimit', e.target.value)}
                            />
                        </Stack>
                    </Box>
                    {
                        isEditing && (
                            <Stack direction={"row"} spacing={1}>
                                <Input
                                    fullWidth
                                    id="LaborCost_Input"
                                    type="number"
                                    value={workOrder.laborCost}
                                    label={TranslateW("workOrderLabor.material-labor-cost")}
                                    onChange={e => handleWorkOrderFieldData(index, 'laborCost', e.target.value)}
                                />
                                <Input
                                    fullWidth
                                    id="MaterialCost_Input"
                                    type="number"
                                    value={workOrder.materialCost}
                                    label={TranslateW("workOrderLabor.material-cost")}
                                    onChange={e => handleWorkOrderFieldData(index, 'materialCost', e.target.value)}
                                />
                                <Input
                                    fullWidth
                                    id="OtherCost_Input"
                                    type="number"
                                    value={workOrder.otherCost}
                                    label={TranslateW("workOrderLabor.material-other-cost")}
                                    onChange={e => handleWorkOrderFieldData(index, 'otherCost', e.target.value)}
                                />
                            </Stack>
                        )
                    }
                    <Box sx={{ display: UserInfo.isTechnician() ? { xs: 'none', lg: 'block' } : 'block' }}>
                        <OnOffSwitch
                            id="ProposalRequired_Toggle"
                            label={TranslateW("proposalRequired")}
                            value={workOrder.proposalRequired}
                            onChange={e => handleWorkOrderFieldData(index, 'proposalRequired', e.target.checked)}
                        />
                    </Box>
                </Stack>
            </Grid>
        </Grid>
    </>
);

export default Form;
